/**
 * How a Locker look is drawn (docs/game-design.md §11): the colour as a hue turn and a
 * saturation, the same in the menus (a CSS filter) and in the fight (a PixiJS colour matrix),
 * and the glove a fighter wears.
 */
import { cosmeticItem, DEFAULT_LOOK, type EffectItem, type Look } from '@caged/shared';
import { assetUrl, type AssetManifest, type GloveManifest, type PetManifest } from './manifest';

/** Hue of a #rrggbb colour in degrees (0 when it has none). */
function hueOf(hex: string): number {
  const n = parseInt(hex.replace('#', ''), 16);
  const r = ((n >> 16) & 255) / 255;
  const g = ((n >> 8) & 255) / 255;
  const b = (n & 255) / 255;
  const max = Math.max(r, g, b);
  const d = max - Math.min(r, g, b);
  if (d === 0) return 0;
  const h = max === r ? ((g - b) / d) % 6 : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  return (h * 60 + 360) % 360;
}

/**
 * The colour item as a hue turn and a saturation for a fighter whose main colour is
 * `fighterColor`: the main colour is turned to the item's hue, so a colour looks the same on
 * every fighter. Null for the fighter's natural colours.
 */
export function colorShift(look: Look | undefined, fighterColor: string): { hue: number; saturate: number } | null {
  const item = cosmeticItem('color', look?.color ?? DEFAULT_LOOK.color);
  if (!item || (item.hue === null && item.saturate === 1)) return null;
  const hue = item.hue === null ? 0 : (((item.hue - hueOf(fighterColor)) % 360) + 540) % 360 - 180;
  return { hue, saturate: item.saturate };
}

/** The CSS filter for a look's colour ("" when natural). */
export function colorCss(look: Look | undefined, fighterColor: string): string {
  const shift = colorShift(look, fighterColor);
  return shift ? `hue-rotate(${shift.hue.toFixed(1)}deg) saturate(${shift.saturate})` : '';
}

/**
 * The same as a 4×5 colour matrix for PixiJS's ColorMatrixFilter: the Filter Effects spec's
 * hueRotate then saturate, as CSS applies them, so the fight matches the menus.
 */
export function colorMatrix(look: Look | undefined, fighterColor: string): number[] | null {
  const shift = colorShift(look, fighterColor);
  if (!shift) return null;
  const a = (shift.hue * Math.PI) / 180;
  const c = Math.cos(a);
  const s = Math.sin(a);
  const h = [
    [0.213 + c * 0.787 - s * 0.213, 0.715 - c * 0.715 - s * 0.715, 0.072 - c * 0.072 + s * 0.928],
    [0.213 - c * 0.213 + s * 0.143, 0.715 + c * 0.285 + s * 0.14, 0.072 - c * 0.072 - s * 0.283],
    [0.213 - c * 0.213 - s * 0.787, 0.715 - c * 0.715 + s * 0.715, 0.072 + c * 0.928 + s * 0.072],
  ];
  const v = shift.saturate;
  const sat = [
    [0.213 + 0.787 * v, 0.715 - 0.715 * v, 0.072 - 0.072 * v],
    [0.213 - 0.213 * v, 0.715 + 0.285 * v, 0.072 - 0.072 * v],
    [0.213 - 0.213 * v, 0.715 - 0.715 * v, 0.072 + 0.928 * v],
  ];
  const m = (i: number, j: number) => sat[i]![0]! * h[0]![j]! + sat[i]![1]! * h[1]![j]! + sat[i]![2]! * h[2]![j]!;
  return [m(0, 0), m(0, 1), m(0, 2), 0, 0, m(1, 0), m(1, 1), m(1, 2), 0, 0, m(2, 0), m(2, 1), m(2, 2), 0, 0, 0, 0, 0, 1, 0];
}

/** The glove a look wears, or null for the fighter's own fists (or a glove this build lacks). */
export function gloveOf(manifest: AssetManifest | null, look: Look | undefined): GloveManifest | null {
  const id = look?.fist ?? DEFAULT_LOOK.fist;
  if (id === DEFAULT_LOOK.fist) return null;
  return manifest?.gloves?.items.find((g) => g.id === id) ?? null;
}

export function gloveUrl(glove: GloveManifest): string {
  return assetUrl(glove.image);
}

/** The effect a look shows, or null for none. */
export function effectOf(look: Look | undefined): EffectItem | null {
  const item = cosmeticItem('effect', look?.effect ?? DEFAULT_LOOK.effect);
  return item && item.style !== 'none' ? item : null;
}

/** The pet a look brings along, or null for none (or a pet this build lacks). */
export function petOf(manifest: AssetManifest | null, look: Look | undefined): PetManifest | null {
  const id = look?.pet ?? DEFAULT_LOOK.pet;
  if (id === DEFAULT_LOOK.pet) return null;
  return manifest?.pets?.items.find((p) => p.id === id) ?? null;
}
